import { isOlderThan } from '@skytech/utils'

import { get, post } from '@/util/auth'
import { isSubscriptionOrderWaitingForUser, SUBSCRIPTION_ORDER_STATUS } from '@/util/orders'

import {
  API_ROUTE_SUBSCRIPTION_ORDER,
  API_ROUTE_SUBSCRIPTION_CANCEL,
  API_ROUTE_SUBSCRIPTION_START,
  API_ROUTE_SUBSCRIPTION_COMPLETE,
  API_ROUTE_SUBSCRIPTION_TERMINATE,
  API_ROUTE_SUBSCRIPTION_SMS,
  API_ROUTE_SUBSCRIPTION_REMAINDER_SMS,
} from './apiRoutes'

type SubscriptionOrder = {
  id: number
  price: number
  status: number
  comment: string
  subscription_service_id: number
  order_date: string // ISO 8601 date string
  order_update: string // ISO 8601 date string
  dealer_comment: string | null
  user_comment: string | null
  currency: string
  subscription_service_name: string
  user: string
  code: string
  activation_number: string | null
  is_new_number: boolean | null
  is_business_sub: boolean
  ssoaid: number
  activation_date: string | null // ISO 8601 date string
  days_to_activation: number | null
  termination_type_id: number | null
  document_id: number | null
  termination_date: string | null // ISO 8601 date string
  days_to_termination: number | null
  birth_date_org_no: string | null
  credit_check_acceptance_given: boolean | null
  org_no: string
  customer: string
  api_supplier_carrier_id: number | null
  activation_user_submission: string | null
  termination_user_submission: string | null
  api_order_id: number | null
  api_supplier_order_status: 'Pending' | 'Held' | 'Cancelled' | 'Failed'
}

function isForManualHandling(order: SubscriptionOrder) {
  const NUMBER_OF_DAYS_FOR_A_STALE_ORDER = 1
  const isOrderStale = isOlderThan(order.order_update, NUMBER_OF_DAYS_FOR_A_STALE_ORDER)

  const rules: {
    name: string
    condition: (order: SubscriptionOrder) => boolean
  }[] = [
    {
      name: 'Error',
      condition: order => order.status === 99,
    },
    {
      name: 'In Progress',
      condition: order =>
        order.status === 1 &&
        ['Pending', 'Held', 'Cancelled', 'Failed'].includes(order.api_supplier_order_status),
    },
    {
      name: 'Not Newly In Progress',
      condition: order => order.status === 1 && order.api_order_id !== null && isOrderStale,
    },
    {
      name: 'Processed By Automation',
      condition: order =>
        order.status === 0 &&
        order.subscription_service_id === 22 &&
        order.api_supplier_carrier_id !== null &&
        isOrderStale &&
        Boolean(
          (!order.is_new_number && order.activation_user_submission !== null) || order.is_new_number
        ),
    },
    {
      name: 'Sub Cancelled And Form Submitted',
      condition: order =>
        order.status === 0 &&
        order.subscription_service_id === 23 &&
        order.api_supplier_carrier_id !== null &&
        order.termination_user_submission !== null &&
        (order.termination_type_id === 0 || order.termination_type_id === 2) &&
        isOrderStale,
    },
    {
      name: 'Product Is A Carrier Automated Product',
      condition: order =>
        [0, 3].includes(order.status) &&
        ![22, 23].includes(order.subscription_service_id) &&
        isOrderStale,
    },
  ]

  return rules.some(rule => rule.condition(order))
}

const getStatusFromRawOrder = (order: SubscriptionOrder) => {
  if (isSubscriptionOrderWaitingForUser(order)) {
    return SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER
  }

  if (order.status === SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER) {
    return SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_DEALER
  }

  if (isForManualHandling(order)) {
    return SUBSCRIPTION_ORDER_STATUS.FOR_MANUAL_HANDLING
  }

  return order.status
}

export const getSubscriptionOrderAPI = async (orderId: number) => {
  const response = await get(API_ROUTE_SUBSCRIPTION_ORDER.replace(':order_id', String(orderId)))

  return transformOrderFromWS(response)
}

const transformOrderFromWS = (order: any) => ({
  ...order,
  status: getStatusFromRawOrder(order),
  ogStatus: order.status,
})

export const postSubscriptionOrderAPI = async ({
  orderId,
  order,
}: {
  orderId: number
  order: any
}) => {
  const response = await post(API_ROUTE_SUBSCRIPTION_ORDER.replace(':order_id', String(orderId)), {
    ...order,
    code: order?.code?.trim(),
  })

  return transformOrderFromWS(response)
}

export const startSubscriptionOrderAPI = async ({
  orderId,
  order,
}: {
  orderId: number
  order: any
}) => {
  const response = await post(
    API_ROUTE_SUBSCRIPTION_START.replace(':order_id', String(orderId)),
    order
  )

  return transformOrderFromWS(response)
}

export const completeSubscriptionOrderAPI = async ({
  orderId,
  order,
}: {
  orderId: number
  order: any
}) => {
  const response = await post(
    API_ROUTE_SUBSCRIPTION_COMPLETE.replace(':order_id', String(orderId)),
    order
  )

  return transformOrderFromWS(response)
}

export const terminateSubscriptionOrderAPI = async ({
  orderId,
  order,
}: {
  orderId: number
  order: any
}) => {
  const response = await post(
    API_ROUTE_SUBSCRIPTION_TERMINATE.replace(':order_id', String(orderId)),
    order
  )

  return transformOrderFromWS(response)
}

export const cancelSubscriptionOrderAPI = async ({
  orderId,
  order,
}: {
  orderId: number
  order: any
}) => {
  const response = await post(
    API_ROUTE_SUBSCRIPTION_CANCEL.replace(':order_id', String(orderId)),
    order
  )

  return transformOrderFromWS(response)
}

export const sendSubscriptionOrderSmsAPI = async ({
  orderId,
  aliasId,
  message,
}: {
  orderId: number
  aliasId: number
  message: string
}) =>
  post(API_ROUTE_SUBSCRIPTION_SMS.replace(':order_id', String(orderId)), {
    aliasId,
    message,
  })

export const sendSubscriptionOrderRemainderSmsAPI = async (orderId: number) =>
  post(API_ROUTE_SUBSCRIPTION_REMAINDER_SMS.replace(':order_id', String(orderId)))
